import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useContext,
} from "react";
import { useFormikContext } from "formik";
import { TextInput } from "@justworkshr/milo-form";
import { SystemIcon } from "@justworkshr/milo-icons";
import parsePhoneNumber, {
  type CountryCode,
  getCountryCallingCode,
} from "libphonenumber-js";

import styles from "./IntlPhoneNumberInput.module.css";
import { SupportedCountriesContext } from "./contexts/supportedCountriesContext";
import { useTranslation } from "react-i18next";

type Props = {
  id?: string;
  className?: string;
  name: string;
  onChange: (event: React.ChangeEvent) => void;
  onBlur: (event: React.FocusEvent) => void;
  value: string;
  countryCode?: string;
};

const IntlPhoneNumberInput = ({
  id,
  className,
  name,
  onBlur,
  countryCode = "CA",
  value,
}: Props) => {
  const { t } = useTranslation();
  const { supportedCountriesData } = useContext(SupportedCountriesContext);
  const supportedCountries = useMemo(
    () =>
      Object.values(supportedCountriesData).map((country) => {
        const phoneCountryCode = `+${getCountryCallingCode(
          country.code as CountryCode
        )}`;
        return {
          countryCode: country.code,
          commonName: country.commonName,
          emojiFlag: country.emojiFlag,
          phoneCountryCode,
        };
      }) || [],
    [supportedCountriesData]
  );

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
    parsePhoneNumber(value)?.country || countryCode
  );
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [flag, setFlag] = useState<string>("");
  const { setFieldValue, setFieldTouched } = useFormikContext();

  const countryPhoneCode = useMemo(() => {
    return `+${getCountryCallingCode(selectedCountryCode as CountryCode)}`;
  }, [selectedCountryCode]);

  useEffect(() => {
    if (value && value.length > 0) {
      const phoneNumber = parsePhoneNumber(value);

      if (phoneNumber) {
        // Update country code from parsed phone number
        if (phoneNumber.country) {
          setSelectedCountryCode(phoneNumber.country);
        }
        setPhoneNumber(phoneNumber.formatNational());
      }
    }
  }, [value]);

  useEffect(() => {
    const strippedPhoneNumber = phoneNumber
      .replace(`${countryPhoneCode}`, "")
      .replace(/\D/g, "");
    const parsedPhoneNumber = parsePhoneNumber(
      `${countryPhoneCode}${strippedPhoneNumber}`
    );
    if (parsedPhoneNumber) {
      setFieldValue(name, parsedPhoneNumber.number, true);
      setPhoneNumber(parsedPhoneNumber.formatNational());
    } else {
      setFieldValue(name, `${countryPhoneCode}${strippedPhoneNumber}`, true);
    }
  }, [name, countryPhoneCode, phoneNumber, setFieldValue]);

  const setFlagByCountryCode = useCallback(
    (code: string) => {
      const country = supportedCountries.find(
        (country) => country.countryCode === code
      );

      if (country) {
        setFlag(country.emojiFlag || "");
      }
    },
    [supportedCountries]
  );

  useEffect(() => {
    // Update flag when selected country changes
    setFlagByCountryCode(selectedCountryCode);
  }, [selectedCountryCode, setFlagByCountryCode]);

  useEffect(() => {
    // Update from countryCode prop when it changes
    if (countryCode) {
      setSelectedCountryCode(countryCode);
    }
  }, [countryCode]);

  useEffect(() => {
    // to show error when switching country codes on a pre-populated field
    if (phoneNumber) {
      setFieldTouched(name, true, true);
    }
  }, [name, phoneNumber, selectedCountryCode, setFieldTouched]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.currentTarget.value);
  };

  return (
    <div className={className}>
      <div className={styles.countrySelectContainer}>
        <select
          className={styles.countryCodeSelect}
          value={selectedCountryCode}
          onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
            setSelectedCountryCode(event.currentTarget.value);
          }}
          required
        >
          {supportedCountries.map((country) => {
            return (
              <option value={country.countryCode} key={country.countryCode}>
                {`${country.emojiFlag} ${
                  country.commonName ? t(country.commonName) : ""
                } ${country.phoneCountryCode}`}
              </option>
            );
          })}
        </select>

        <div className={styles.countryFlag}>{flag}</div>
        <SystemIcon iconName="chevron-down" />
        <span className={styles.countryCode}>{countryPhoneCode}</span>

        <div className={styles.phoneNumberInput}>
          <TextInput
            id={id}
            name={name}
            value={phoneNumber}
            onBlur={onBlur}
            onChange={handleChange}
          />
        </div>
      </div>
    </div>
  );
};

export default IntlPhoneNumberInput;
